import {useQuery} from "@apollo/client";
import classNames from "classnames";
import {useMemo} from "react";
import {useFormContext} from "react-hook-form";
import {FiExternalLink} from "react-icons/fi";

import {Button} from "@/components/button/Button.tsx";
import {FormLabel} from "@/components/form/FormLabel";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {UPTIME_FEEDBACK_URL} from "@/lib/constants/links.ts";
import {ServiceField} from "@/lib/constants/service.ts";
import {getFormError} from "@/lib/form/form.utils.ts";
import {CloudStatusMetasCount, GQL_THIRD_PARTY_COMPONENTS_COUNT, ThirdPartyService} from "@/lib/queries/checks.ts";
import {ThirdPartyMonitoringType} from "@/lib/types/checks.ts";
import {ThirdPartyGroup} from "@/pages/checks/components/form/fields/components/ThirdPartyGroup.tsx";
import {ThirdyPartyPopularGroups} from "@/pages/checks/components/form/fields/components/ThirdyPartyPopularGroups.tsx";
import {ThirdPartyGroupSelectField} from "@/pages/checks/components/form/fields/ThirdPartyGroupSelectField.tsx";

export const CheckThirdPartyFormv2 = () => {
  const {
    data: countData,
    loading: countLoading,
    previousData: countPreviousData,
  } = useQuery<CloudStatusMetasCount>(GQL_THIRD_PARTY_COMPONENTS_COUNT);
  const currentCountData = countLoading ? countPreviousData : countData;
  const count = useMemo(() => currentCountData?.cloudStatusMetasCount.count || 0, [currentCountData]);

  const {
    watch,
    setValue,
    clearErrors,
    formState: {errors},
  } = useFormContext();
  const selectedGroup = watch(ServiceField.THIRD_PARTY_GROUP) as ThirdPartyService | null;
  const selectedGroupError = getFormError(ServiceField.THIRD_PARTY_GROUP, errors);
  const checkId = watch(ServiceField.ID);
  const onGroupChange = (group: ThirdPartyService | null, setGroup?: boolean) => {
    if (checkId) {
      return;
    }
    if (group?.id !== selectedGroup?.id) {
      if (setGroup) {
        setValue(ServiceField.THIRD_PARTY_GROUP, group, {shouldDirty: true});
      }
      clearErrors(ServiceField.THIRD_PARTY_GROUP);
      setValue(
        ServiceField.THIRD_PARTY_MONITORING_TYPE,
        (group?.components_count ?? 0) < 100 ? ThirdPartyMonitoringType.ALL : ThirdPartyMonitoringType.SPECIFIC
      );
      setValue(ServiceField.THIRD_PARTY_COMPONENTS, [], {shouldDirty: true});
      clearErrors(ServiceField.THIRD_PARTY_COMPONENTS);
    }
  };

  return (
    <div className="pulseMaxWidthXL mt-4">
      <FormLabel forId={ServiceField.THIRD_PARTY_GROUP} required>
        Select Service to Monitor
      </FormLabel>
      {selectedGroup && (
        <>
          <FormSubtext error={selectedGroupError?.toString()} />
          <ThirdPartyGroup onChange={onGroupChange} />
        </>
      )}
      <div className={classNames({"d-none": !!selectedGroup})}>
        <div className="d-flex gap-3 flex-md-row flex-column mb-4">
          <div className="flex-grow-1">
            <ThirdPartyGroupSelectField
              name={ServiceField.THIRD_PARTY_GROUP}
              placeholder={`Search from ${count ? Math.floor(count / 1_000) + ",000+ " : ""}third-party services...`}
              onChange={onGroupChange}
            />
          </div>
          <div className="flex-shrink-0">
            <Button variant="secondary" onClick={() => window.open(UPTIME_FEEDBACK_URL, "_blank")} className="w-100">
              Request a New Service <Icon icon={FiExternalLink} />
            </Button>
          </div>
        </div>
        <ThirdyPartyPopularGroups onChange={onGroupChange} />
      </div>
    </div>
  );
};
